'use client';

import { useCallback, type MouseEvent, type ReactNode } from 'react';

import Link from '@/components/ui/Link';
import Image from '@/components/ui/Image';
import { usePathname, useSearchParams } from '@/lib/router';
// Phosphor is the app's product iconset — lighter, and a closer match to the
// redesign concept than Lucide. ChartLineUp stands in for "investments".
import {
  ArrowsLeftRightIcon,
  CreditCardIcon,
  ChartLineUpIcon as InvestmentsIcon,
  LinkIcon,
  SignOutIcon,
  ScrollIcon,
  TargetIcon,
  WalletIcon,
  RepeatIcon,
  HouseIcon,
  UserCircleIcon,
  ChatCircleTextIcon,
  CaretUpDownIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TooltipRoot, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { track, useFeatureFlags } from '@/lib/telemetry';
import { PROBLEM_REPORT_FLAG, emitOpenProblemReport } from '@/lib/problemReport';
import { ScopeSwitcher } from '@/components/spaces/ScopeSwitcher';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { spaceNavItems, spaceTelemetryTarget } from '@/lib/spaces/navigation';

interface AppSidebarProps {
  onLogout: () => void;
  userName: string;
  userEmail?: string;
  userImageUrl?: string;
}

function initials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

type NavItem = {
  href: string;
  label: string;
  icon: PhosphorIcon;
  matchPrefix?: string;
  matchPrefixes?: string[];
};

const HOME_ITEM: NavItem = { href: '/', label: 'Início', icon: HouseIcon };
const GENERAL: NavItem[] = [HOME_ITEM];

const FLOW: NavItem[] = [
  { href: '/transactions', label: 'Transações', icon: ArrowsLeftRightIcon, matchPrefix: '/transactions' },
  { href: '/invoices', label: 'Faturas', icon: CreditCardIcon, matchPrefixes: ['/invoices', '/faturas'] },
];

const PLANNING: NavItem[] = [
  { href: '/budget', label: 'Categorias', icon: WalletIcon, matchPrefix: '/budget' },
  { href: '/metas', label: 'Metas', icon: TargetIcon, matchPrefix: '/metas' },
  { href: '/recurring-payments', label: 'Recorrentes', icon: RepeatIcon, matchPrefix: '/recurring-payments' },
];

const ASSETS: NavItem[] = [
  { href: '/investments', label: 'Investimentos', icon: InvestmentsIcon, matchPrefix: '/investments' },
];

const ACCOUNT: NavItem[] = [
  // Perfil lives only in the user account menu (sidebar footer); Conexões stays here.
  { href: '/connections', label: 'Conexões', icon: LinkIcon, matchPrefix: '/connections' },
];

type VariantStyle = {
  sidebarClass: string;
  headerTitleClass: string;
  groupLabelClass: string;
  active: string;
  inactive: string;
  footer: string;
};

const VARIANTS: Record<string, VariantStyle> = {
  slatePill: {
    // Redesign shell: a detached floating card (variant="floating") — white panel,
    // 20px radius, soft green-tinted elevation, ~14px inset margin. Overrides the
    // shadcn floating defaults (rounded-lg / shadow-none) on the inner panel.
    sidebarClass: cn(
      'p-3.5 group-data-[collapsible=icon]:p-2',
      // Glass panel — same recipe as the AppHeader (translucent #FCFCFC @ .82 +
      // 10px backdrop blur) so header and sidebar read as one frosted surface and
      // a future theme re-tints both via the same token. The floating-card shape
      // (20px radius, sidebar shadow, hairline border) still matches the proposal.
      '[&_[data-slot=sidebar-inner]]:bg-surface-glass',
      '[&_[data-slot=sidebar-inner]]:backdrop-blur-[10px]',
      '[&_[data-slot=sidebar-inner]]:rounded-[var(--radius-3xl)]',
      '[&_[data-slot=sidebar-inner]]:border-border-subtle',
      '[&_[data-slot=sidebar-inner]]:shadow-[var(--shadow-3)]',
    ),
    headerTitleClass: 'text-[16px] font-bold leading-tight text-text-primary',
    groupLabelClass: 'mb-1.5 font-mono text-3xs font-semibold uppercase tracking-[0.14em] text-text-secondary h-auto group-data-[collapsible=icon]:hidden',
    // Colors + radius + 20px icon — safe in BOTH states (collapsed keeps shadcn's
    // !size-8/!p-2 centering). The expanded-only density (padding/gap) is added in
    // renderMenuItem so it doesn't strip the collapsed icon-rail rules via tw-merge.
    active: 'cursor-pointer rounded-[var(--radius-md)] font-semibold bg-sidebar-accent text-brand-primary hover:bg-sidebar-accent hover:text-brand-primary [&>svg]:size-5 group-data-[collapsible=icon]:mx-auto',
    inactive: 'cursor-pointer rounded-[var(--radius-md)] font-semibold text-text-secondary hover:bg-surface-sunken hover:text-text-primary [&>svg]:size-5 group-data-[collapsible=icon]:mx-auto',
    footer: 'text-text-secondary rounded-[var(--radius-md)] hover:bg-surface-sunken hover:text-text-primary font-semibold',
  },
};

// Bug-report affordance above the footer divider. Self-guards on the flag (returns
// null when off). Expanded → a full CTA card; collapsed → an icon button with a
// tooltip so reporting stays reachable in the icon rail. The onboarding-tour anchor
// (data-onboarding-problem-report) rides on the button in BOTH states.
function SidebarReportProblemCta() {
  const { state } = useSidebar();
  const enabled = useFeatureFlags(PROBLEM_REPORT_FLAG);
  if (!enabled) return null;

  const handleReport = () => emitOpenProblemReport('sidebar');

  if (state === 'collapsed') {
    return (
      <div className="shrink-0 px-2 pb-2">
        <TooltipRoot>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleReport}
              data-onboarding-problem-report
              aria-label="Relatar um problema"
              className="mx-auto flex size-8 cursor-pointer items-center justify-center rounded-[var(--radius-md)] text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary"
            >
              <ChatCircleTextIcon className="size-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">Relatar um problema</TooltipContent>
        </TooltipRoot>
      </div>
    );
  }

  return (
    <div className="shrink-0 px-2 pb-2">
      <button
        type="button"
        onClick={handleReport}
        data-onboarding-problem-report
        className="flex w-full cursor-pointer items-start gap-2.5 rounded-[var(--radius-lg)] bg-surface-sunken p-2.5 text-left transition-colors hover:bg-border-subtle"
      >
        <ChatCircleTextIcon className="mt-0.5 size-4 shrink-0 text-text-tertiary" />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-xs font-semibold leading-tight text-text-secondary">Encontrou algo errado?</span>
          <span className="text-2xs leading-snug text-text-tertiary">Relate um problema para a gente</span>
        </span>
      </button>
    </div>
  );
}

type SidebarNavigationProps = {
  renderItem: (item: NavItem) => ReactNode;
  groupLabelClass: string;
};

function SpaceSidebarNavigation({ renderItem, groupLabelClass }: SidebarNavigationProps) {
  const { scope, space } = useSpaceScope();
  const searchParams = useSearchParams();
  if (scope !== 'space') return null;
  if (!space) return <p className="px-4 py-2 text-xs text-text-secondary group-data-[collapsible=icon]:sr-only">Selecione um espaço disponível.</p>;

  const items = spaceNavItems(space, 'sidebar', searchParams);
  const home = items.filter((item) => item.id === 'home').map(renderItem);
  const finances = items.filter((item) => item.id !== 'home' && item.id !== 'settings').map(renderItem);
  const settings = items.filter((item) => item.id === 'settings').map(renderItem);

  return (
    <>
      <SidebarGroup className="py-1">
        <SidebarMenu>{home}</SidebarMenu>
      </SidebarGroup>
      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Do espaço</SidebarGroupLabel>
        <SidebarMenu>{finances}</SidebarMenu>
      </SidebarGroup>
      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Espaço</SidebarGroupLabel>
        <SidebarMenu>{settings}</SidebarMenu>
      </SidebarGroup>
    </>
  );
}

function PersonalSidebarNavigation({ renderItem, groupLabelClass }: SidebarNavigationProps) {
  const { scope, enabled } = useSpaceScope();
  if (scope !== 'personal') return null;
  const account = enabled ? [{ href: '/spaces-rules', label: 'Espaços e regras', icon: UsersThreeIcon }, ...ACCOUNT] : ACCOUNT;
  return (
    <>
      <SidebarGroup className="py-1">
        <SidebarMenu>{GENERAL.map(renderItem)}</SidebarMenu>
      </SidebarGroup>

      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Fluxo de Caixa</SidebarGroupLabel>
        <SidebarMenu>{FLOW.map(renderItem)}</SidebarMenu>
      </SidebarGroup>

      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Planejamento</SidebarGroupLabel>
        <SidebarMenu>{PLANNING.map(renderItem)}</SidebarMenu>
      </SidebarGroup>

      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Patrimônio</SidebarGroupLabel>
        <SidebarMenu>{ASSETS.map(renderItem)}</SidebarMenu>
      </SidebarGroup>

      <SidebarGroup className="pt-4 pb-1 group-data-[collapsible=icon]:pt-1">
        <SidebarGroupLabel className={groupLabelClass}>Conta</SidebarGroupLabel>
        <SidebarMenu>{account.map(renderItem)}</SidebarMenu>
      </SidebarGroup>
    </>
  );
}

function PersonalSidebarBrand({ collapsed, titleClass }: { collapsed: boolean; titleClass: string }) {
  const { enabled, scope } = useSpaceScope();
  if (enabled || scope !== 'personal') return null;
  if (collapsed) return <Image src="/favicon.png" alt="Vértice" width={34} height={34} />;
  return (
    <div className="flex items-center gap-2.5">
      <Image src="/favicon.png" alt="Vértice" width={34} height={34} />
      <div className="flex flex-col leading-none">
        <span className={titleClass}>Vértice</span>
        <span className="mt-0.5 text-2xs text-text-tertiary">Conta pessoal</span>
      </div>
    </div>
  );
}

export function AppSidebar({ onLogout, userName, userEmail, userImageUrl }: AppSidebarProps) {
  const pathname = usePathname();
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { scope } = useSpaceScope();

  // One delegated handler for every nav item — the clicked anchor's href IS the
  // nav target, so items need no per-item closure.
  const handleNavClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    const target = event.currentTarget.getAttribute('href');
    if (target) track('nav.clicked', { surface: 'sidebar', target: spaceTelemetryTarget(target), scope });
  }, [scope]);

  const isActive = (href: string, prefix?: string, prefixes?: string[]) => {
    if (href === '/') return pathname === '/';
    if (prefixes?.some((p) => pathname.startsWith(p))) return true;
    if (prefix) return pathname.startsWith(prefix);
    return pathname.startsWith(href);
  };

  const onboardingIdFor = (item: NavItem) => {
    if (item.href === '/') return 'home';
    if (item.href === '/invoices') return 'invoices';
    if (item.href === '/metas') return 'goals';
    if (item.href === '/recurring-payments') return 'recurring';
    return item.href.replace('/', '');
  };

  const renderMenuItem = (item: NavItem, v: VariantStyle) => {
    const active = isActive(item.href, item.matchPrefix, item.matchPrefixes);
    const Icon = item.icon;
    return (
      <SidebarMenuItem key={item.href}>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={item.label}
          className={cn(
            active ? v.active : v.inactive,
            // Expanded-only density (9px×11px padding, 12px gap); omitted when
            // collapsed so the base !size-8/!p-2 icon-rail centering survives.
            !collapsed && 'h-auto gap-3 px-[11px] py-[9px] text-sm',
          )}
        >
          <Link href={item.href} aria-current={active ? 'page' : undefined} onClick={handleNavClick} data-onboarding-nav={onboardingIdFor(item)}>
            <Icon />
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>

        {item.matchPrefix === '/investments' && /^\/investments\/[^/]+/.test(pathname) && !collapsed && (
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive>
                <Link href={pathname}>
                  <ScrollIcon size={12} />
                  <span>Movimentações</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        )}

        {item.matchPrefix === '/metas' && /^\/metas\//.test(pathname) && !collapsed && (
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive>
                <Link href={pathname}>
                  <ScrollIcon size={12} />
                  <span>Edição</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    );
  };

  const renderVariant = (variantKey: keyof typeof VARIANTS) => {
    const v = VARIANTS[variantKey];
    const renderItem = (item: NavItem) => renderMenuItem(item, v);
    return (
      <Sidebar variant="floating" collapsible="icon" className={v.sidebarClass} data-tokens="v2" data-onboarding-sidebar>
        {/* Brand sits on the same 64px band as the AppHeader top bar (vertically aligned
            with the header controls), but with no divider line — the sidebar reads as a
            clean panel, separated from the main area by surface contrast, not borders. */}
        <SidebarHeader
          className={cn(
            // shrink-0: the brand band stays pinned at full height when the nav below
            // scrolls on a short viewport (never squashed to reclaim space).
            'h-16 shrink-0 flex-row items-center px-4',
            collapsed && 'justify-center px-0',
          )}
        >
          <ScopeSwitcher collapsed={collapsed} />
          <PersonalSidebarBrand collapsed={collapsed} titleClass={v.headerTitleClass} />
        </SidebarHeader>

        {/* No separator lines: each labelled section is set apart by whitespace + its
            uppercase label alone. Collapsed/icon mode drops the extra top padding so the
            icon rail stays tight (the labels are hidden there). */}
        {/* Scroll the nav internally when the viewport is too short to fit every group:
            the header/CTA/footer are shrink-0 siblings that stay pinned, so this region
            absorbs the overflow. overflow-x-hidden keeps the collapse/expand width
            animation from flashing a horizontal scrollbar. The icon-rail override is
            needed because the base primitive hard-clips (overflow-hidden) in collapsed
            mode — restore vertical scroll there too so a short viewport is reachable in
            both states. */}
        <SidebarContent className="overflow-y-auto overflow-x-hidden group-data-[collapsible=icon]:overflow-y-auto">
          <SpaceSidebarNavigation renderItem={renderItem} groupLabelClass={v.groupLabelClass} />
          <PersonalSidebarNavigation renderItem={renderItem} groupLabelClass={v.groupLabelClass} />
        </SidebarContent>

        {/* CTA above the footer divider (outside the profile area); self-guards. */}
        <SidebarReportProblemCta />

        {/* shrink-0: the user card + report CTA stay pinned to the bottom and reachable
            even when the nav overflows and scrolls. */}
        <SidebarFooter className="shrink-0 border-t border-sidebar-border">
          {/* User account menu — identity lives here (Perfil / Sair). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Conta"
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--radius-md)] p-1.5 text-left transition-colors hover:bg-surface-sunken data-[state=open]:bg-surface-sunken',
                  collapsed && 'justify-center p-1',
                )}
              >
                {userImageUrl ? (
                  <span className="size-[34px] shrink-0 overflow-hidden rounded-[var(--radius-sm)]">
                    <Image
                      src={userImageUrl}
                      alt={userName}
                      width={34}
                      height={34}
                      className="size-full object-cover"
                    />
                  </span>
                ) : (
                  <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-linear-to-br from-brand-600 to-brand-800 text-xs font-bold text-white">
                    {initials(userName)}
                  </span>
                )}
                {!collapsed && (
                  <>
                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="truncate text-sm font-semibold text-text-primary">{userName}</span>
                      {userEmail && (
                        <span className="truncate font-mono text-2xs text-text-secondary">{userEmail}</span>
                      )}
                    </div>
                    <CaretUpDownIcon size={14} className="shrink-0 text-text-tertiary" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              sideOffset={8}
              data-tokens="v2"
              // Light hairline + elevation: a white menu on the near-white canvas needs a defined
              // edge (the shadow alone leaves the top edge invisible — a11y contrast issue).
              // Carries its own data-tokens scope because Radix portals it out of the sidebar.
              className="w-56 rounded-[var(--radius-xl)] border border-border bg-surface p-1.5 shadow-[var(--shadow-3)]"
            >
              <DropdownMenuItem asChild>
                <Link href="/profile" className="cursor-pointer rounded-[var(--radius-sm)]">
                  <UserCircleIcon />
                  Perfil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer rounded-[var(--radius-sm)] text-error focus:text-error"
                onSelect={onLogout}
              >
                <SignOutIcon />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>

      </Sidebar>
    );
  };

  return renderVariant('slatePill');
}
